import { Component, OnDestroy, OnInit } from '@angular/core';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { Subject, takeUntil } from 'rxjs';
import { FeatureUsageResponse, FeatureUsageRowDTO, FeatureUsageVM } from '../../models/usage';
import { FEATURE_FLAG_LABELS, FeatureFlag, QuotaUnit } from '../../models/tenant';
import { UsageService } from '../../../services/usage.service';

/** Beyond this many tenants the per-tenant chart stops being readable and the tail is grouped. */
const CHART_TENANT_LIMIT = 10;

/** How many months back the picker offers. A year covers every question anyone asks of a bill. */
const MONTHS_OFFERED = 12;

/** Where a tenant stops being comfortable and starts being worth a phone call. */
const WARN_AT_PERCENT = 80;

/**
 * What the metered features cost, per tenant, for one month.
 *
 * <p>Its own component rather than more of UsageComponent: the two tabs share a page but not a
 * request, a period, or a single line of state, and the users report is already long enough.
 */
@Component({
  selector: 'app-feature-usage',
  templateUrl: './feature-usage.component.html',
  styleUrls: ['./feature-usage.component.scss']
})
export class FeatureUsageComponent implements OnInit, OnDestroy {
  loading = true;
  refreshing = false;
  error: string | null = null;

  generatedAt: string | null = null;
  period = '';
  periodOptions: string[] = [];

  rows: FeatureUsageVM[] = [];
  filteredRows: FeatureUsageVM[] = [];

  // Filters run over the already-loaded array — there is no request behind them, so the input can
  // never be disabled or unmounted mid-keystroke. Only the period reloads.
  searchTerm = '';
  featureFilter: '' | FeatureFlag = '';
  overQuotaOnly = false;

  featureOptions: { value: FeatureFlag; label: string }[] = [];

  /** One tile per metered feature, plus the count of tenants close to their line. */
  totals: {
    voiceMinutes: number;
    voiceTenants: number;
    botRequests: number;
    botTenants: number;
    tokens: number;
    nearLimit: number;
  } = { voiceMinutes: 0, voiceTenants: 0, botRequests: 0, botTenants: 0, tokens: 0, nearLimit: 0 };

  chartData: { name: string; series: { name: string; value: number }[] }[] = [];
  chartTailNote: string | null = null;

  readonly usedSeriesLabel = 'Used';
  readonly remainingSeriesLabel = 'Remaining';

  /**
   * The same blue/orange as the users tab, and for the same reason: the pair separates at ΔE 27+
   * under protanopia and deuteranopia. Every bar also carries its figure in text and the table
   * below repeats it, so colour never carries meaning alone.
   */
  readonly usageScheme: Color = {
    name: 'featureUsage',
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#2f7fd0', '#f0932b']
  };

  private destroy$ = new Subject<void>();

  constructor(private usageService: UsageService) {}

  ngOnInit(): void {
    this.periodOptions = this.buildPeriods();
    this.period = this.periodOptions[0];
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.refreshing = true;
    this.error = null;

    this.usageService.getFeatureUsage(this.period).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.apply(response);
        this.loading = false;
        this.refreshing = false;
      },
      error: error => {
        console.error('Error loading feature usage:', error);
        this.error = 'Failed to load feature usage. Please try again.';
        this.loading = false;
        this.refreshing = false;
      }
    });
  }

  onPeriodChange(): void {
    this.load();
  }

  private apply(response: FeatureUsageResponse): void {
    this.generatedAt = response.generatedAt;
    this.period = response.period;

    this.rows = (response.data || []).map(row => this.toViewModel(row));

    this.featureOptions = Array.from(new Set(this.rows.map(r => r.feature)))
      .map(feature => ({ value: feature, label: FEATURE_FLAG_LABELS[feature] ?? feature }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.buildTotals();
    this.buildChart();
    this.applyFilters();
  }

  private toViewModel(row: FeatureUsageRowDTO): FeatureUsageVM {
    // Never divide by an allowance of zero — a tenant whose limit is nothing but who somehow spent
    // something is at 100%, not at infinity.
    const percentUsed = row.allowance > 0
      ? Math.round((row.quantity / row.allowance) * 100)
      : (row.quantity > 0 ? 100 : 0);

    return {
      ...row,
      featureLabel: FEATURE_FLAG_LABELS[row.feature] ?? row.feature,
      usedLabel: this.inAdminUnits(row.quantity, row.unit),
      limitLabel: this.inAdminUnits(row.allowance, row.unit),
      onFreePlan: row.ownLimit === null,
      percentUsed,
      severity: percentUsed >= 100 ? 'over' : (percentUsed >= WARN_AT_PERCENT ? 'warn' : 'ok')
    };
  }

  /**
   * Booking units back into the ones an admin reads. Voice is counted in seconds and sold in
   * minutes; a request is a request.
   */
  private inAdminUnits(value: number, unit: QuotaUnit): string {
    if (unit === QuotaUnit.MINUTES) {
      return `${Math.round(value / 60)} min`;
    }
    return `${value}`;
  }

  private buildTotals(): void {
    const voice = this.rows.filter(r => r.feature === FeatureFlag.FEATURE_VOICE_MESSAGES);
    const bot = this.rows.filter(r => r.feature === FeatureFlag.FEATURE_CHAT_BOT);

    this.totals = {
      voiceMinutes: Math.round(voice.reduce((sum, r) => sum + r.quantity, 0) / 60),
      voiceTenants: voice.filter(r => r.quantity > 0).length,
      botRequests: bot.reduce((sum, r) => sum + r.quantity, 0),
      botTenants: bot.filter(r => r.quantity > 0).length,
      tokens: this.rows.reduce((sum, r) => sum + r.tokens, 0),
      // Only tenants that actually have the feature — a tenant at "100% of nothing" is not news.
      nearLimit: this.rows.filter(r => r.enabled && r.percentUsed >= WARN_AT_PERCENT).length
    };
  }

  private buildChart(): void {
    // Only tenants doing something: a chart of zeroes tells nobody anything, and the table below
    // still lists everyone.
    const spending = this.rows
      .filter(row => row.quantity > 0)
      .sort((a, b) => b.percentUsed - a.percentUsed);

    const shown = spending.slice(0, CHART_TENANT_LIMIT);
    const tail = spending.slice(CHART_TENANT_LIMIT);

    // Percent rather than raw units: one chart cannot share an axis between minutes and requests,
    // and "how close to the line" is the question the chart is here to answer anyway.
    this.chartData = shown.map(row => ({
      name: `${row.tenantName} · ${row.featureLabel}`,
      series: [
        { name: this.usedSeriesLabel, value: Math.min(100, row.percentUsed) },
        { name: this.remainingSeriesLabel, value: Math.max(0, 100 - row.percentUsed) }
      ]
    }));

    // Never let a chart quietly drop rows — say what was left out.
    this.chartTailNote = tail.length
      ? `Top ${CHART_TENANT_LIMIT} by share of allowance used; ${tail.length} more are in the table below.`
      : null;
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    this.filteredRows = this.rows.filter(row => {
      if (term && !`${row.tenantName} ${row.featureLabel}`.toLowerCase().includes(term)) {
        return false;
      }
      if (this.featureFilter && row.feature !== this.featureFilter) {
        return false;
      }
      if (this.overQuotaOnly && row.percentUsed < WARN_AT_PERCENT) {
        return false;
      }
      return true;
    }).sort((a, b) => b.percentUsed - a.percentUsed
      || b.quantity - a.quantity
      || a.tenantName.localeCompare(b.tenantName));
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.featureFilter = '';
    this.overQuotaOnly = false;
    this.applyFilters();
  }

  trackByRow(index: number, row: FeatureUsageVM): string {
    return `${row.tenantId}-${row.feature}`;
  }

  /** `YYYY-MM` for this month and the eleven before it, newest first. */
  private buildPeriods(): string[] {
    const now = new Date();
    const periods: string[] = [];
    for (let back = 0; back < MONTHS_OFFERED; back++) {
      const month = new Date(now.getFullYear(), now.getMonth() - back, 1);
      periods.push(`${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`);
    }
    return periods;
  }
}
